import React, { useCallback, useContext, useEffect, useState } from "react";

import { Edit } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import {
  Alert,
  Box,
  CircularProgress,
  FormLabel,
  Grid,
  IconButton,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import Modal from "@mui/material/Modal";
import clsx from "clsx";
import { useParams } from "react-router-dom";
import mainStyles from "styles";

import { Tier } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/common_pb";
import { SubscriptionComponent } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/components_pb";
import {
  SubscriptionData,
  SubscriptionState,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/subscriptions_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";

import { toUUID } from "../../../../helpers/s12-helpers";
import {
  getSubscriptionComponentBySubId,
  updateSubscriptionComponent as updateSubscriptionComponentApi,
} from "api/endpoints/components";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import { DEFAULT_SNACKBAR_DURATION } from "constants/index";
import {
  MIN_FULL_SEATS,
  MIN_LITE_SEATS,
  PREMIUM_SEATS_MAX_SEATS_DEFAULT,
  PREMIUM_SEATS_MAX_SEATS_UPPER_DEFAULT,
  userSeatsToString,
} from "constants/seats";
import { SubscriptionSeatUsageContext } from "context/organisation-context";
import { PermissionsContext } from "context/permissions-context";
import { SnackbarContext } from "context/snackbar-context";
import { SubscriptionActionsContext } from "context/subscriptions-action-context";
import { UrlParamTypes } from "types/org-display";

interface ChangeComponentQuantityZuoraProps {
  subscription: SubscriptionData;
  refreshOrgData: () => void;
}

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "40rem",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 2,
  p: 4,
};

const ChangeComponentQuantityZuora = ({
  subscription,
  refreshOrgData,
}: ChangeComponentQuantityZuoraProps) => {
  const classes = mainStyles();
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const subId = subscription?.subscriptionId;
  const state = subscription?.state || SubscriptionState.UNSPECIFIED;

  const { subscriptionSeatUsage } = useContext(SubscriptionSeatUsageContext);
  const { hasGroups, hasPermissions, permissions } =
    useContext(PermissionsContext);
  const canEditQuantity =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.UPDATE_SUBSCRIPTION_COMPONENT_QUANTITY]);

  // temporary until we reach phase 3 of sensor units
  // https://safetyculture.atlassian.net/browse/BSA-10201
  const canAllocateSensorPlanUnit = hasGroups([
    "App - Business Tools - AR",
    "App - Business Tools - BSA",
    "Business Tools Preview - Engineer", // non-production okta group
  ]);

  const canAllocatePremiumComponentUpperBound = hasGroups([
    "App - Business Tools - AR",
    "App - Business Tools - BSA",
    "Business Tools Preview - Engineer", // non-production okta group
  ]);

  const MAX_SEATS = canAllocatePremiumComponentUpperBound
    ? PREMIUM_SEATS_MAX_SEATS_UPPER_DEFAULT
    : PREMIUM_SEATS_MAX_SEATS_DEFAULT;

  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [showProcessingPage, setShowProcessingPage] = useState(false);
  const [
    selectedComponentNewTotalQuantity,
    setSelectedComponentNewTotalQuantity,
  ] = useState<number>(0);
  const [
    selectedComponentAssignedQuantity,
    setSelectedComponentAssignedQuantity,
  ] = useState<number>(0);
  const [selectedComponent, setSelectedComponent] =
    useState<SubscriptionComponent>();
  const [selectedComponentQuantity, setSelectedComponentQuantity] =
    useState<number>(0);
  const [components, setComponents] = useState<SubscriptionComponent[]>([]);
  const [selectedComponentLabel, setSelectedComponentLabel] =
    useState<string>("");
  const [selectedMinSeat, setSelectedMinSeat] = useState<number>(0);
  const [, setOpenConfirmDialog] = useState(false);
  const [memo, setMemo] = useState<string>(
    "Update subscription user seats quota"
  );
  const { isSubscriptionActionLoading, setIsSubscriptionActionLoading } =
    useContext(SubscriptionActionsContext);

  const { showError } = useContext(SnackbarContext);
  const isFreeOrg = subscription?.tier === Tier.FREE;
  const subActive = subscription?.active === true;
  const isCanceledOrUnspecified =
    state === SubscriptionState.CANCELED ||
    state === SubscriptionState.UNSPECIFIED;

  const getSubscriptionComponents = useCallback(() => {
    if (!subId || isFreeOrg) {
      return;
    }

    getSubscriptionComponentBySubId(
      {
        subId: toUUID(subId),
      },
      callOptionsHeaders(regionCode, roleId)
    )
      .then((res) => {
        const subComponents = res.subscriptionComponents;
        setComponents(subComponents);
      })
      .catch(showError)
      .finally(() => {
        setIsLoading(false);
      });
  }, [subId, regionCode, roleId, showError, isFreeOrg]);

  useEffect(() => {
    if (isFreeOrg) {
      return;
    }
    getSubscriptionComponents();
  }, [
    getSubscriptionComponents,
    isFreeOrg,
    subscriptionSeatUsage?.premiumSeatUsageSummary?.purchasedSeats,
  ]);

  const displaySubscriptionComponents = () => {
    if (components && components.length > 0) {
      return components
        .sort((a, b) => a.id - b.id)
        .map((c) => getComponentDisplay(c));
    } else {
      return (
        <>Unable to load seats info from Zuora, please refresh the page.</>
      );
    }
  };

  const getComponentDisplay = (component: SubscriptionComponent) => {
    let occupiedSeats = 0;
    let minSeat = 0;
    const { name, quantity } = component;
    let seatLabel = userSeatsToString[name];
    let unit = "seat";

    if (name.includes("FULL_SEAT")) {
      seatLabel = "Full";
    }
    if (name.includes("LITE_SEAT")) {
      seatLabel = "Lite";
    }
    if (name.includes("Sensor")) {
      unit = "unit";
      seatLabel = "Sensor";
    }
    if (seatLabel === "Full") {
      occupiedSeats =
        subscriptionSeatUsage?.premiumSeatUsageSummary?.occupiedSeats || 0;
      minSeat = MIN_FULL_SEATS;
    }
    if (seatLabel === "Free") {
      occupiedSeats =
        subscriptionSeatUsage?.collaboratorSeatUsageSummary?.occupiedSeats || 0;
    }
    if (seatLabel === "Lite") {
      occupiedSeats =
        subscriptionSeatUsage?.liteSeatUsageSummary?.occupiedSeats || 0;
      minSeat = MIN_LITE_SEATS;
    }

    if (!seatLabel) {
      return null;
    }

    return (
      <Typography key={name} align="left" variant="body2">
        <b
          data-testid={seatLabel.toLowerCase() + "-" + unit + "-quantity-label"}
        >
          {seatLabel}:
        </b>{" "}
        <label data-testid={seatLabel.toLowerCase() + "-" + unit + "-quantity"}>
          {quantity}
        </label>
        {seatLabel !== "Free" &&
        canEditQuantity &&
        (seatLabel !== "Sensor" || canAllocateSensorPlanUnit) ? (
          <IconButton
            href=""
            target="_blank"
            rel="noopener"
            size="small"
            color="primary"
            data-testid={
              seatLabel.toLowerCase() + "-" + unit + "-quantity-edit-button"
            }
            style={{
              position: "relative",
              bottom: 2,
              left: 5,
              padding: 1,
            }}
            onClick={() => {
              setShowModal(true);
              setSelectedComponent(component);
              setSelectedComponentQuantity(quantity);
              setSelectedComponentNewTotalQuantity(quantity);
              setSelectedComponentAssignedQuantity(occupiedSeats);
              setSelectedComponentLabel(seatLabel);
              setSelectedMinSeat(minSeat);
            }}
          >
            <Edit fontSize="small" />
          </IconButton>
        ) : null}
      </Typography>
    );
  };

  const updateSubscriptionComponent = async (
    componentPriceId: number,
    newQuantity: number,
    memo: string
  ): Promise<boolean> => {
    setIsSubscriptionActionLoading(true);
    const updated = await updateSubscriptionComponentApi(
      {
        subId: toUUID(subId),
        componentPricePointId: componentPriceId,
        quantity: newQuantity,
        memo,
        organizationId: toUUID(roleId),
        componentType: selectedComponent?.componentType || 0,
      },
      callOptionsHeaders(regionCode, roleId)
    ).catch((error) => {
      setShowModal(false);
      showError(error);
      return false;
    });
    setShowProcessingPage(false);
    setIsSubscriptionActionLoading(false);
    if (updated) {
      setShowModal(false);
      setShowSuccessSnackbar(true);
      refreshOrgData();
      getSubscriptionComponents();
      return true;
    } else {
      return false;
    }
  };

  const isAllowedToChangeQuantity: boolean =
    selectedComponentNewTotalQuantity >= selectedComponentAssignedQuantity &&
    selectedComponentNewTotalQuantity !== selectedComponentQuantity &&
    selectedComponentNewTotalQuantity <= MAX_SEATS &&
    selectedComponentNewTotalQuantity >= selectedMinSeat;

  const updateQuantityForm = () => {
    const renderErrorMustBeMoreThanAssignedSeatsAlert = () => {
      if (
        selectedComponentNewTotalQuantity < selectedComponentAssignedQuantity
      ) {
        return (
          <Alert
            severity="warning"
            variant="filled"
            data-testid="error-must-be-more-than-assigned-seats"
            className={clsx(classes.spaceBottomSmall)}
          >
            The total seat must be more than the assigned seat.
          </Alert>
        );
      }
      return null;
    };

    const renderErrorExceedMaxSeatsAlert = () => {
      if (selectedComponentNewTotalQuantity > MAX_SEATS) {
        return (
          <Alert
            severity="warning"
            variant="filled"
            data-testid="error-exceed-max-seats"
            className={clsx(classes.spaceBottomSmall)}
          >
            The total seat should not exceed {MAX_SEATS}.
          </Alert>
        );
      }
      return null;
    };

    return (
      <ConfirmDialog
        title="Update Seat Quantity"
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
        open={showModal}
        setOpen={setOpenConfirmDialog}
        onConfirm={() => {
          setShowProcessingPage(true);
          updateSubscriptionComponent(
            selectedComponent?.componentPrice[0]?.id as number,
            selectedComponentNewTotalQuantity,
            memo
          );
        }}
        onCancel={() => handleModalClose()}
        disabled={isSubscriptionActionLoading || !isAllowedToChangeQuantity}
      >
        <Box sx={{ width: "100%", paddingTop: "0.5rem" }}>
          <FormLabel component="legend">
            <b>{selectedComponentLabel} seats: </b>
            {selectedComponentQuantity} total,{" "}
            {selectedComponentAssignedQuantity} assigned
          </FormLabel>
          <TextField
            fullWidth
            margin="normal"
            label="Enter the new total seats"
            variant={"outlined"}
            type="number"
            onChange={(e) =>
              setSelectedComponentNewTotalQuantity(parseInt(e.target.value))
            }
            value={selectedComponentNewTotalQuantity}
            InputProps={{
              inputProps: {
                min: selectedMinSeat,
                max: MAX_SEATS,
                "data-testid": "enter-new-total-quantity-field",
              },
            }}
          />
        </Box>
        <Box sx={{ width: "100%", paddingTop: "0.5rem" }}>
          <TextField
            fullWidth
            margin="normal"
            label="Memo"
            variant={"outlined"}
            type="string"
            onChange={(e) => setMemo(e.target.value)}
            value={memo}
            InputProps={{
              inputProps: {
                "data-testid": "enter-memo",
              },
            }}
          />
        </Box>
        <Box sx={{ width: "100%", paddingTop: "0.5rem" }}>
          {renderErrorMustBeMoreThanAssignedSeatsAlert()}
          {renderErrorExceedMaxSeatsAlert()}
        </Box>
      </ConfirmDialog>
    );
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedComponentNewTotalQuantity(0);
  };

  if (!subscription || isFreeOrg || !subActive || isCanceledOrUnspecified) {
    return null;
  }

  return (
    <>
      {isLoading ? (
        <CircularProgress
          color="primary"
          variant="indeterminate"
          thickness={4}
          size={25}
          data-testid="loading-icon"
        />
      ) : (
        <>{displaySubscriptionComponents()}</>
      )}
      {showModal && (
        <Modal
          open={showModal}
          onClose={handleModalClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          data-testid="change-component-quantity-modal"
        >
          <Box sx={style}>
            {!showProcessingPage ? (
              <>{updateQuantityForm()}</>
            ) : (
              <>
                <Grid
                  container
                  justifyContent="center"
                  className={clsx(
                    classes.spaceTopSmall,
                    classes.spaceRightMedium
                  )}
                >
                  <CircularProgress
                    color="primary"
                    variant="indeterminate"
                    thickness={4}
                    size={25}
                    data-testid="loading-icon"
                  />
                </Grid>
              </>
            )}
          </Box>
        </Modal>
      )}
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={showSuccessSnackbar}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={() => {
          setShowSuccessSnackbar(false);
        }}
        data-testid="synced-seats-success-toast"
      >
        <Alert
          icon={<CheckIcon fontSize="inherit" />}
          severity="success"
          variant="filled"
        >
          Zuora Total Seats and SC Total Seats Updated Successfully
        </Alert>
      </Snackbar>
    </>
  );
};

export default ChangeComponentQuantityZuora;
